import * as XLSX from 'xlsx-js-style';
import { buildCategoryDisplayRows } from './stockControlUtils';

const TITLE = 'Control de repuestos';
const COL_MODEL = 0;
const COL_DESC = 1;
const COL_CODE1 = 2;
const COL_CODE2 = 3;
const COL_CODE3 = 4;
const COL_CODE4 = 5;
const COL_STOCK = 6;
const COL_COUNT = 7;

const STYLE_TITLE = {
  font: { bold: true, sz: 14 },
  alignment: { horizontal: 'center', vertical: 'center' },
};

const STYLE_HEADER = {
  font: { bold: true },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
};

const STYLE_MODEL = {
  alignment: { vertical: 'center', wrapText: true },
};

const setCell = (ws, r, c, value, style) => {
  const ref = XLSX.utils.encode_cell({ r, c });
  const isNum = typeof value === 'number' && !Number.isNaN(value);
  ws[ref] = {
    v: value,
    t: isNum ? 'n' : 's',
    ...(style ? { s: style } : {}),
  };
};

/** Filas planas ordenadas por modelo para exportar un control finalizado. */
export function flattenControlRows(template, session) {
  const rows = [];
  (template?.categories || []).forEach((category) => {
    buildCategoryDisplayRows(category, session).forEach((row) => rows.push(row));
  });
  rows.sort((a, b) => {
    const ma = (a.model || '').localeCompare(b.model || '', 'es');
    if (ma !== 0) return ma;
    return (a.description || '').localeCompare(b.description || '', 'es');
  });
  return rows;
}

const groupRowsByModel = (allRows) => {
  const groups = [];
  allRows.forEach((row) => {
    const model = row.model || 'Sin modelo';
    const last = groups[groups.length - 1];
    if (last && last.model === model) {
      last.rows.push(row);
    } else {
      groups.push({ model, rows: [row] });
    }
  });
  return groups;
};

const stockValue = (row) => {
  const v =
    row.finalStock != null && !Number.isNaN(Number(row.finalStock))
      ? row.finalStock
      : row.systemStock ?? 0;
  return Number(v);
};

/**
 * Exporta un control finalizado a Excel (.xlsx) con estilos.
 * Encabezado «Nro de Pieza» combinado sobre 4 columnas (3+3+3+resto).
 */
export function exportStockControlSessionExcel(snapshot, meta = {}) {
  const template = snapshot?.template;
  const session = snapshot?.session;
  if (!template || !session) return;

  const allRows = flattenControlRows(template, session);
  const groups = groupRowsByModel(allRows);

  const ws = {};
  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COL_COUNT - 1 } },
    { s: { r: 1, c: COL_CODE1 }, e: { r: 1, c: COL_CODE4 } },
  ];
  const range = { s: { r: 0, c: 0 }, e: { r: 1, c: COL_COUNT - 1 } };

  setCell(ws, 0, 0, TITLE, STYLE_TITLE);
  setCell(ws, 1, COL_MODEL, 'Modelo', STYLE_HEADER);
  setCell(ws, 1, COL_DESC, 'Descripción', STYLE_HEADER);
  setCell(ws, 1, COL_CODE1, 'Nro de Pieza', STYLE_HEADER);
  setCell(ws, 1, COL_STOCK, 'Stock', STYLE_HEADER);

  let rowIndex = 2;

  groups.forEach((group) => {
    const startRow = rowIndex;
    group.rows.forEach((row, idx) => {
      const stock = stockValue(row);
      if (idx === 0) {
        setCell(ws, rowIndex, COL_MODEL, group.model, STYLE_MODEL);
      } else {
        setCell(ws, rowIndex, COL_MODEL, '');
      }
      setCell(ws, rowIndex, COL_DESC, row.description ?? '');
      setCell(ws, rowIndex, COL_CODE1, row.code1 ?? '');
      setCell(ws, rowIndex, COL_CODE2, row.code2 ?? '');
      setCell(ws, rowIndex, COL_CODE3, row.code3 ?? '');
      setCell(ws, rowIndex, COL_CODE4, row.code4 ?? '');
      setCell(ws, rowIndex, COL_STOCK, stock);
      rowIndex += 1;
    });
    const endRow = rowIndex - 1;
    if (endRow > startRow) {
      merges.push({ s: { r: startRow, c: COL_MODEL }, e: { r: endRow, c: COL_MODEL } });
    }
  });

  range.e.r = Math.max(1, rowIndex - 1);
  ws['!ref'] = XLSX.utils.encode_range(range);
  ws['!merges'] = merges;
  ws['!cols'] = [
    { wch: 22 },
    { wch: 40 },
    { wch: 8 },
    { wch: 8 },
    { wch: 8 },
    { wch: 14 },
    { wch: 10 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Control');

  const date =
    meta.date ||
    (snapshot.session?.finishedAt
      ? String(snapshot.session.finishedAt).slice(0, 10)
      : new Date().toISOString().slice(0, 10));
  const idSuffix = meta.id ? `_${meta.id}` : '';
  XLSX.writeFile(wb, `control_repuestos${idSuffix}_${date}.xlsx`);
}
